import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthContext } from '../../../context/AuthContext';
import { ActivateModelDialog } from '../../components/common/ActivateModelDialog';
import { ArchiveModelDialog } from '../../components/common/ArchiveModelDialog';
import { CreateModelDialog } from '../../components/common/CreateModelDialog';
import { RowActionsMenu, RowActionsMenuItem } from '../../components/common/RowActionsMenu';
import { ARCHIVED_STATUS_STYLES } from '../../constants';
import { EvalApiService } from '../../services/EvalApiService';
import { EvalModel } from '../../types';
import { fmtIso } from '../../utils';

interface RayModelListPageProps {
  modelType: 'OD' | 'VE' | 'OOS';
  title: string;
  description?: string;
}

export const RayModelListPage: React.FC<RayModelListPageProps> = ({
  modelType,
  title,
  description,
}) => {
  const { token } = useAuthContext();
  const [models, setModels] = useState<EvalModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('active');
  const [activateTarget, setActivateTarget] = useState<EvalModel | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<EvalModel | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { items } = await EvalApiService.listModels(token ?? '', { model_type: modelType });
      setModels(items);
    } catch {
      setModels([]);
    } finally {
      setLoading(false);
    }
  }, [token, modelType]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = models.filter((m) =>
    statusFilter === 'archived' ? m.is_archived : statusFilter === 'active' ? !m.is_archived : true
  );

  return (
    <div className="page-content">
      <div className="eval-page-header">
        <div>
          <div className="eval-page-title">{title}</div>
          <div className="eval-page-sub">{description ?? ''}</div>
        </div>
        <button className="eval-btn" onClick={() => setShowCreate(true)}>
          Add model
        </button>
      </div>

      <div className="eval-filters">
        <select
          className="eval-filter-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="archived">Archived</option>
        </select>
      </div>

      {loading ? (
        <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Loading...</div>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Version</th>
              <th>Status</th>
              <th>Assignments</th>
              <th>Registered At</th>
              <th>Updated At</th>
              <th>Description</th>
              <th style={{ width: 40 }}></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((m) => (
              <tr
                key={m.model_id}
                style={
                  m.is_default
                    ? { background: 'color-mix(in srgb, var(--accent) 14%, transparent)' }
                    : undefined
                }
              >
                <td
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 12,
                    fontWeight: 600,
                    boxShadow: m.is_default ? 'inset 4px 0 0 var(--accent)' : undefined,
                  }}
                >
                  <Link to={`/eval/models/${m.model_id}`} style={{ color: 'var(--accent)' }}>
                    {m.model_name}
                  </Link>
                </td>
                <td>
                  <span
                    style={{
                      ...ARCHIVED_STATUS_STYLES[m.is_archived ? 'archived' : 'active'],
                      fontSize: 12,
                    }}
                  >
                    {m.is_archived ? 'Archived' : 'Active'}
                  </span>
                </td>
                <td style={{ fontSize: 12 }}>
                  {(() => {
                    if (m.is_default) {
                      return (
                        <span
                          style={{
                            padding: '2px 6px',
                            borderRadius: 4,
                            background: 'var(--accent)',
                            color: 'var(--surface)',
                            fontSize: 10,
                            fontWeight: 700,
                            letterSpacing: 0.5,
                          }}
                        >
                          DEFAULT
                        </span>
                      );
                    }
                    const chains = m.assigned_chains ?? [];
                    if (chains.length === 0) {
                      return <span style={{ color: 'var(--text-secondary)' }}>Unused</span>;
                    }
                    return (
                      <span
                        title={chains.map((c) => c.chain_name).join(', ')}
                        style={{
                          padding: '2px 8px',
                          borderRadius: 4,
                          border: '1px solid var(--border)',
                          color: 'var(--text-primary)',
                          cursor: 'default',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {chains.length} chain{chains.length === 1 ? '' : 's'}
                      </span>
                    );
                  })()}
                </td>
                <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  {fmtIso(m.registered_at)}
                </td>
                <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  {m.updated_at ? fmtIso(m.updated_at) : '—'}
                </td>
                <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  {m.description ?? '—'}
                </td>
                <td style={{ textAlign: 'center' }}>
                  {m.is_archived ? (
                    <RowActionsMenu>
                      <RowActionsMenuItem
                        onClick={async () => {
                          await EvalApiService.patchModel(token ?? '', m.model_id, {
                            is_archived: false,
                          });
                          load();
                        }}
                      >
                        Unarchive
                      </RowActionsMenuItem>
                    </RowActionsMenu>
                  ) : !m.is_default ? (
                    <RowActionsMenu>
                      <RowActionsMenuItem onClick={() => setActivateTarget(m)}>
                        Set as default
                      </RowActionsMenuItem>
                      {(m.assigned_chains ?? []).length === 0 && (
                        <RowActionsMenuItem danger onClick={() => setArchiveTarget(m)}>
                          Archive
                        </RowActionsMenuItem>
                      )}
                    </RowActionsMenu>
                  ) : null}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td
                  colSpan={7}
                  style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: 24 }}
                >
                  No {modelType} models match the selected filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {activateTarget && (
        <ActivateModelDialog
          model={activateTarget}
          onConfirm={async () => {
            await EvalApiService.setDefaultModel(token ?? '', activateTarget.model_id);
            setActivateTarget(null);
            load();
          }}
          onCancel={() => setActivateTarget(null)}
        />
      )}

      {archiveTarget && (
        <ArchiveModelDialog
          model={archiveTarget}
          onConfirm={async () => {
            await EvalApiService.patchModel(token ?? '', archiveTarget.model_id, {
              is_archived: true,
            });
            setArchiveTarget(null);
            load();
          }}
          onCancel={() => setArchiveTarget(null)}
        />
      )}

      {showCreate && (
        <CreateModelDialog
          modelType={modelType}
          onCreate={async (body) => {
            await EvalApiService.createModel(token ?? '', body);
            setShowCreate(false);
            load();
          }}
          onCancel={() => setShowCreate(false)}
        />
      )}
    </div>
  );
};

export const OdModelListPage: React.FC = () => (
  <RayModelListPage
    modelType="OD"
    title="OD Models"
    description="Object Detection models for product recognition."
  />
);

export const PrModelListPage: React.FC = () => (
  <RayModelListPage
    modelType="VE"
    title="VE Models"
    description="Visual Embedding models for product recognition."
  />
);

export const OosModelListPage: React.FC = () => (
  <RayModelListPage
    modelType="OOS"
    title="OOS Models"
    description="Out-of-Stock models for product recognition."
  />
);
